import { convertToPlainText } from '../../../utils/richContent/convertToPlainText';
import { isOpportunityRichHtmlFieldKey } from '../../../utils/richContent/opportunityRichHtmlFields';
import { booleanToYesNo } from '../../../utils/yesNoBoolean';
import { formatWorkloadTypeLabel } from '../../../validation/opportunityProjectFieldsSchema';

function formatTeamMemberSummaryValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  let parsed: unknown = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return '';
    }
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return '';
  }

  return parsed
    .map(item => {
      if (typeof item === 'string') {
        return item.trim();
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const email = record.email ?? record.userEmail ?? record.id;
        const name = record.name;
        if (name && email && String(name) !== String(email)) {
          return `${String(name)} (${String(email)})`;
        }
        return String(email ?? name ?? '').trim();
      }
      return String(item).trim();
    })
    .filter(Boolean)
    .join(', ');
}

function rackAdditionalMergedDisplay(data: Record<string, unknown>): unknown {
  const primary = data.rackAdditionalRackInformation;
  const legacy = data.rackAdditionalInformation;
  const nonEmpty = (v: unknown) =>
    v != null && typeof v === 'string' && v.trim() !== '' ? v : undefined;
  return nonEmpty(primary) ?? nonEmpty(legacy) ?? primary ?? legacy;
}

export function siteInformationSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  // #1012 Item 1: siteDataCenterFlooring now stores 'Yes'/'No' canonically
  // (was 'Raised'/'Slab' — see migration 084). No special-case branch needed.
  return data[dataField] as string | number | boolean | null | undefined;
}

export function facilityPowerSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  return data[dataField] as string | number | boolean | null | undefined;
}

export function racksSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  if (dataField === 'rackStatus') {
    const status = data[dataField];
    if (status === 'existing') return 'Existing';
    if (status === 'new') return 'New';
    return status != null ? String(status) : undefined;
  }
  if (
    dataField === 'rackSeismicRated' ||
    dataField === 'rackRequiresLiquidToChip'
  ) {
    return booleanToYesNo(data[dataField]);
  }
  if (dataField === 'rackAdditionalRackInformation') {
    return rackAdditionalMergedDisplay(data) as
      | string
      | number
      | boolean
      | null
      | undefined;
  }
  return data[dataField] as string | number | boolean | null | undefined;
}

function stringOrEmptyForRatingField(
  data: Record<string, unknown>,
  dataField:
    | 'smallUpsCapacity'
    | 'smallUpsLoadPercentage'
    | 'smallUpsBatteryCabinetQty'
    | 'smallUpsExpectedLoadGrowth',
): string {
  const v = data[dataField];
  if (v != null && v !== '') {
    return String(v);
  }
  return '';
}

export function smallUpsSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  if (
    dataField === 'smallUpsCapacity' ||
    dataField === 'smallUpsLoadPercentage' ||
    dataField === 'smallUpsBatteryCabinetQty' ||
    dataField === 'smallUpsExpectedLoadGrowth'
  ) {
    return stringOrEmptyForRatingField(
      data,
      dataField as
        | 'smallUpsCapacity'
        | 'smallUpsLoadPercentage'
        | 'smallUpsBatteryCabinetQty'
        | 'smallUpsExpectedLoadGrowth',
    );
  }
  if (
    dataField === 'smallUpsBackupGenerator' ||
    dataField === 'smallUpsMaintenanceBypassRequired' ||
    dataField === 'smallUpsLoadOnGenerator'
  ) {
    return booleanToYesNo(data[dataField]);
  }
  return data[dataField] as string | number | boolean | null | undefined;
}

export function coolingReadinessSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  if (dataField === 'coolingRearDoorHexDesired') {
    return booleanToYesNo(data.coolingRearDoorHexDesired);
  }
  return data[dataField] as string | number | boolean | null | undefined;
}

export function liquidCoolingSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  if (
    dataField === 'lcLiquidToChipDesired' ||
    dataField === 'lcVertivSuppliesRowManifolds' ||
    dataField === 'lcVertivSuppliesRackManifolds'
  ) {
    return booleanToYesNo(data[dataField]);
  }
  return data[dataField] as string | number | boolean | null | undefined;
}

export function monitoringSummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  return data[dataField] as string | number | boolean | null | undefined;
}

export function opportunitySummaryRowValue(
  data: Record<string, unknown>,
  dataField: string,
): string | number | boolean | null | undefined {
  if (isOpportunityRichHtmlFieldKey(dataField)) {
    const value = data[dataField];
    return typeof value === 'string' ? convertToPlainText(value) : '';
  }
  if (dataField === 'requestorPhone') {
    return (data.requestorPhone || data.phone) as
      | string
      | number
      | boolean
      | null
      | undefined;
  }
  if (dataField === 'region') {
    const r = data.region;
    if (r == null || r === '') {
      return r as string | null | undefined;
    }
    return String(r).toUpperCase();
  }
  if (dataField === 'oppProjectScope') {
    const v = data.oppProjectScope;
    if (Array.isArray(v)) {
      return v.join(', ');
    }
    return v as string | number | boolean | null | undefined;
  }
  if (dataField === 'oppWorkloadType') {
    // Multi-select since migration 092, stored as an array or JSON-array string.
    // Each pick renders through its friendly display label — the STORED value is
    // the verbose canonical string that gates the child NVL config rows, so it
    // must not change. See WORKLOAD_TYPE_DISPLAY_LABELS.
    const raw = data.oppWorkloadType;
    let arr: unknown = raw;
    if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      try {
        arr = JSON.parse(raw);
      } catch {
        arr = raw;
      }
    }
    if (Array.isArray(arr)) {
      return arr.map(item => formatWorkloadTypeLabel(String(item))).join(', ');
    }
    return typeof raw === 'string'
      ? formatWorkloadTypeLabel(raw)
      : (raw as string | number | boolean | null | undefined);
  }
  if (dataField === 'oppSiteAssessmentRequired') {
    const v = data.oppSiteAssessmentRequired;
    if (typeof v === 'boolean' || v === 1 || v === 0) {
      return booleanToYesNo(v);
    }
    if (v == null) {
      return undefined;
    }
    return String(v).trim();
  }
  if (
    dataField === 'vertivSponsors' ||
    dataField === 'vertivContributors' ||
    dataField === 'partnerContributors'
  ) {
    return formatTeamMemberSummaryValue(data[dataField]);
  }
  return data[dataField] as string | number | boolean | null | undefined;
}
